import { SIDER_WIDTH_STORAGE_KEY, useTheme } from '@zhiyue/core';
import React from 'react';
import {
  DEFAULT_SIDER_WIDTH,
  HEADER_HEIGHT,
  MAX_SIDER_WIDTH,
  MIN_SIDER_WIDTH,
  RESIZE_HANDLE_HITBOX,
} from './layoutConstants';
import styles from './SiderResizeHandle.module.less';

export {
  DEFAULT_SIDER_WIDTH,
  MAX_SIDER_WIDTH,
  MIN_SIDER_WIDTH,
  SIDER_WIDTH_STORAGE_KEY,
};
export const clampSiderWidth = (width: number) =>
  Math.round(Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, width)));
export const getStoredSiderWidth = () => {
  if (typeof window === 'undefined') return DEFAULT_SIDER_WIDTH;
  const raw = window.localStorage.getItem(SIDER_WIDTH_STORAGE_KEY);
  const width = Number(raw);
  return raw === null || raw.trim() === '' || !Number.isFinite(width)
    ? DEFAULT_SIDER_WIDTH
    : clampSiderWidth(width);
};
const persistWidth = (width: number) =>
  window.localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(width));

type Props = {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
};

// 平台 1.2.0 没有宽度范围参数；沿用平台存储键和主题，兼容当前壳层尺寸规范。
export default function SiderResizeHandle({
  enabled,
  siderWidth,
  onResizeWidth,
}: Props) {
  const { navTheme } = useTheme();
  const [boundary, setBoundary] = React.useState({
    x: -1,
    top: HEADER_HEIGHT,
    hitTop: HEADER_HEIGHT + 56,
  });
  const [dragging, setDragging] = React.useState(false);
  const drag = React.useRef({ x: 0, width: siderWidth, pointerId: -1 });
  const frame = React.useRef<number | null>(null);
  const pendingWidth = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled) return;
    const sider = document.querySelector<HTMLElement>('.ant-pro-sider');
    const sync = () => {
      const rect = sider?.getBoundingClientRect();
      if (!rect || rect.width <= 90 || window.innerWidth <= 768) {
        setBoundary((previous) =>
          previous.x === -1 ? previous : { ...previous, x: -1 },
        );
        return;
      }
      const header = document.querySelector('.ant-pro-layout-header');
      const top = Math.ceil(
        Math.max(rect.top, header?.getBoundingClientRect().bottom ?? 0),
      );
      const trigger = sider?.querySelector('.ant-pro-sider-collapsed-button');
      const hitTop = Math.max(
        top + 56,
        Math.ceil((trigger?.getBoundingClientRect().bottom ?? top) + 12),
      );
      const x = Math.round(rect.right);
      setBoundary((previous) =>
        previous.x === x && previous.top === top && previous.hitTop === hitTop
          ? previous
          : { x, top, hitTop },
      );
    };
    sync();
    window.addEventListener('resize', sync);
    const observer =
      sider && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(sync)
        : null;
    if (sider) observer?.observe(sider);
    return () => {
      window.removeEventListener('resize', sync);
      observer?.disconnect();
    };
  }, [enabled, siderWidth]);

  React.useEffect(() => {
    if (!dragging) return;
    const commit = () => {
      frame.current = null;
      const width = pendingWidth.current;
      pendingWidth.current = null;
      if (width === null) return;
      persistWidth(width);
      onResizeWidth(width);
    };
    const move = (event: PointerEvent) => {
      if (event.pointerId !== drag.current.pointerId) return;
      pendingWidth.current = clampSiderWidth(
        drag.current.width + event.clientX - drag.current.x,
      );
      if (frame.current === null)
        frame.current = window.requestAnimationFrame(commit);
    };
    const stop = () => {
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
      commit();
      setDragging(false);
    };
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
      frame.current = null;
      pendingWidth.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
    };
  }, [dragging, onResizeWidth]);

  React.useEffect(() => {
    if (!enabled || boundary.x < 0) setDragging(false);
  }, [enabled, boundary.x]);
  if (!enabled || boundary.x < 0) return null;
  const style = {
    '--zy-handle-left': `${boundary.x - RESIZE_HANDLE_HITBOX / 2}px`,
    '--zy-handle-top': `${boundary.hitTop}px`,
    '--zy-handle-width': `${RESIZE_HANDLE_HITBOX}px`,
    '--sidebar-visual-top': `${boundary.top}px`,
  } as React.CSSProperties;
  return (
    <div
      className={`${styles.boundary} ${navTheme === 'realDark' ? styles.dark : ''}`}
    >
      {dragging && <div className={styles.dragMask} />}
      <div
        className={styles.hitBox}
        style={style}
        role="slider"
        tabIndex={0}
        aria-label="Resize sidebar"
        aria-orientation="horizontal"
        aria-valuemin={MIN_SIDER_WIDTH}
        aria-valuemax={MAX_SIDER_WIDTH}
        aria-valuenow={Math.round(siderWidth)}
        data-dragging={dragging}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          drag.current = {
            x: event.clientX,
            width: siderWidth,
            pointerId: event.pointerId,
          };
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setDragging(true);
        }}
        onKeyDown={(event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key))
            return;
          event.preventDefault();
          const delta =
            (event.shiftKey ? 24 : 8) * (event.key === 'ArrowLeft' ? -1 : 1);
          const width =
            event.key === 'Home'
              ? MIN_SIDER_WIDTH
              : event.key === 'End'
                ? MAX_SIDER_WIDTH
                : clampSiderWidth(siderWidth + delta);
          persistWidth(width);
          onResizeWidth(width);
        }}
      />
    </div>
  );
}
